import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { cn } from '@strivelabs/ui-core';

interface RichTextRendererProps {
  content: string;
  className?: string;
  debug?: boolean; // Add debug prop to help troubleshoot
}

export const RichTextRenderer: React.FC<RichTextRendererProps> = ({ 
  content, 
  className,
  debug = false
}) => {
  // Process the content to convert escaped characters to actual characters
  const processedContent = React.useMemo(() => {
    if (!content) return '';
    
    // More comprehensive escape sequence handling
    let processed = content
      // Handle double newlines first (paragraph breaks)
      .replace(/\\n\\n/g, '\n\n')
      // Handle single newlines
      .replace(/\\n/g, '\n')
      // Handle other common escape sequences
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r')
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      // Handle double backslashes last to avoid interfering with other replacements
      .replace(/\\\\/g, '\\');
    
    // Additional processing: ensure proper paragraph spacing for markdown
    processed = processed
      // Normalize multiple consecutive newlines to double newlines (paragraph breaks)
      .replace(/\n{3,}/g, '\n\n')
      // Ensure headings at the start of content have proper spacing
      .replace(/^(#{1,6}\s)/g, '$1')
      // Ensure headings have proper spacing when they follow content
      .replace(/\n(#{1,6}\s)/g, '\n\n$1')
      // Ensure content after headings has proper spacing
      .replace(/(#{1,6}.*)\n([^#\n])/g, '$1\n\n$2')
      // Fix any cases where we might have triple newlines after our processing
      .replace(/\n{3,}/g, '\n\n');
    
    // Debug logging to help troubleshoot
    if (debug) {
      console.log('RichTextRenderer Debug:');
      console.log('Original content:', JSON.stringify(content.substring(0, 200) + '...'));
      console.log('Processed content:', JSON.stringify(processed.substring(0, 200) + '...'));
      console.log('Has \\n\\n in original:', content.includes('\\n\\n'));
      console.log('Has actual newlines in processed:', processed.includes('\n\n'));
    }
    
    return processed.trim();
  }, [content, debug]);

  return (
    <div className={cn(
      "prose prose-sm max-w-none font-sans",
      // Maintain the parent's text size but allow headings to be larger
      "[&>h1]:text-lg [&>h1]:font-bold [&>h1]:mt-4 [&>h1]:mb-2 [&>h1]:text-gray-900",
      "[&>h2]:text-base [&>h2]:font-bold [&>h2]:mt-4 [&>h2]:mb-2 [&>h2]:text-gray-900",
      "[&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mt-3 [&>h3]:mb-1 [&>h3]:text-gray-900",
      "[&>h4]:text-sm [&>h4]:font-semibold [&>h4]:mt-3 [&>h4]:mb-1 [&>h4]:text-gray-900",
      "[&>h5]:text-xs [&>h5]:font-semibold [&>h5]:mt-2 [&>h5]:mb-1 [&>h5]:text-gray-900",
      "[&>h6]:text-xs [&>h6]:font-semibold [&>h6]:mt-2 [&>h6]:mb-1 [&>h6]:text-gray-900",
      // Paragraph styles - improved spacing and sizing
      "[&>p]:my-2 [&>p]:leading-relaxed [&>p]:text-current [&>p]:text-sm",
      "[&>p:first-child]:mt-0 [&>p:last-child]:mb-0",
      // List styles - improved bullet visibility and spacing
      "[&>ul]:my-2 [&>ul]:list-none [&>ul]:pl-0",
      "[&>ol]:my-2 [&>ol]:list-decimal [&>ol]:pl-5",
      "[&>ul>li]:relative [&>ul>li]:pl-5 [&>ul>li]:my-1 [&>ul>li]:text-current [&>ul>li]:leading-relaxed",
      "[&>ul>li]:before:content-['•'] [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:text-gray-600 [&>ul>li]:before:font-bold [&>ul>li]:before:text-sm",
      "[&>ol>li]:my-1 [&>ol>li]:text-current [&>ol>li]:leading-relaxed [&>ol>li]:pl-1",
      // Nested lists
      "[&_ul_ul]:my-1 [&_ul_ul]:ml-4",
      "[&_ol_ol]:my-1 [&_ol_ol]:ml-4",
      // Strong and emphasis
      "[&_strong]:font-semibold [&_strong]:text-custom-text-primary",
      "[&_em]:italic [&_em]:text-gray-700",
      // Code styles
      "[&_code]:bg-gray-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono",
      "[&>pre]:bg-gray-50 [&>pre]:border [&>pre]:border-gray-200 [&>pre]:p-3 [&>pre]:rounded [&>pre]:my-3",
      "[&>pre_code]:bg-transparent [&>pre_code]:p-0",
      // Blockquote styles
      "[&>blockquote]:border-l-4 [&>blockquote]:border-blue-500 [&>blockquote]:bg-blue-50 [&>blockquote]:pl-4 [&>blockquote]:py-2 [&>blockquote]:my-3",
      "[&>blockquote]:text-blue-900 [&>blockquote]:italic",
      // Link styles
      "[&_a]:text-blue-600 [&_a]:no-underline hover:[&_a]:underline [&_a]:font-medium",
      // Remove default margins for cleaner integration
      "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
      className
    )}>
      <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{processedContent}</ReactMarkdown>
    </div>
  );
};